import React, { useState, useMemo, Suspense, useCallback, useContext} from 'react';
import getSymbolFromCurrency from 'currency-symbol-map';
import MiniProductReviewsBlock from './MiniProductReviewsBlock';
import { Link, useNavigate } from 'react-router-dom';
import { any, bool, number, oneOfType, string } from 'prop-types';
import ResponsiveImage from './ResponsiveImage';
import AppContext from './context/AppContext';
import { OverlayTrigger, Button } from 'react-bootstrap';
import { CartContext } from './context/CartContext';
import { ToastContext } from './context/ToastContext';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';
import protectCopyRight from '../helpers/protectCopyRight';
import LoadingFallback from './LoadingFallback';

const ProductQuickView = React.lazy(() => import('./ProductQuickView'));

/**
    CONFIGURATION
*/
const EAGER_LOADED_IMAGE_COUNT = 4;

export default function SearchPageProductListItem({ isMobile, title, handle, imageUrl, price, compareAtPrice, availableForSale, searchLoading, avgRating, totalReviews, isFeatured, inventoryLevelId, availableQuantity, bypassNegativeQuantity, view, index, vendor }) {
    const { appContext } = useContext(AppContext);
    const productPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.PRODUCT_PATH });
    const productBasePath = productPath[0].pathBeforeParams;
    const [toggle, setToggle] = useState(false);
    const appConfig = appContext.config.themeConfig;
    const navigate = useNavigate();

    const handleQuickView = useCallback((e) => {
        e.stopPropagation();
        setToggle(true);
    }, []);

    const renderProductQuickView = useCallback((props) => (
        <Suspense
            fallback={<LoadingFallback />}>
            <ProductQuickView
                OverlayTriggerProps={props}
                handle={handle}
                handleToggle={setToggle}
            />
        </Suspense>
    ), [handle]);

    const {
        cartState,
        cartUpdating,
        addLineItem,
        setShowCartOverlay,
    } = useContext(CartContext);
    const { sendMessage } = useContext(ToastContext);

    const addedQuantity = useMemo(() => {
        if (!cartState) {
            return 0;
        }
        return cartState.filter((i) => i.inventoryLevelId === inventoryLevelId).at(0)?.quantity ?? 0;
    }, [cartState, inventoryLevelId]);

    const quantity = useMemo(() => parseInt((bypassNegativeQuantity ? Infinity : availableQuantity) - addedQuantity), [bypassNegativeQuantity, availableQuantity, addedQuantity]);

    const onAddToCartClick = async (e) => {
        e.stopPropagation();

        try {
            const productResponse = await fetch(new URL(`/api/products/handle/${handle}`, process.env.APP_URL));
            const productData = await productResponse.json();

            if (!productData.product) {
                sendMessage('Product information unavailable', 'danger');
                return;
            }

            const product = productData.product;
            const variant = product.Variants?.find((v) => v.availableForSale) || product.Variants?.[0];

            if (!variant?.availableInventoryLevels?.length) {
                sendMessage('Product is currently unavailable', 'danger');
                return;
            }

            const inventoryLevel = variant.availableInventoryLevels[0];

            const result = await addLineItem(inventoryLevel.id, 1);
            if (result.success) {
                sendMessage('Product added to cart', 'success');
                setShowCartOverlay(true);
            } else {
                sendMessage(result?.error || 'Failed to add item to cart', 'danger');
            }
        } catch (e) {
            sendMessage(e.message, 'error');
        }
    };

    return (<button
        className={`search-page--product-list-item${searchLoading === true ? ' loading' : ''} ${view}`}
        onClick={() => {
            navigate(`${productBasePath}/${handle}`);
        }}>
        <OverlayTrigger
            show={toggle}
            overlay={renderProductQuickView}
        >
            <button
                style={{
                    all: 'unset',
                    display: 'block',
                    width: '100%',
                    height: '100%',
                }}
                onContextMenu={protectCopyRight}>
                <div
                    className={'product-quick-view-overlay-trigger'}></div>
            </button>
        </OverlayTrigger>

        {isFeatured === true && (<span
            className={'search-page-list-item--featured'}>Featured</span>)}
        <figure
            className={'search-page--product-list-item-image'}
            data-product-item-image>
            <Button
                className={'search-page--product-list-item-quick-view'}
                onClick={handleQuickView}
            >
                Quick View
            </Button>
            <ResponsiveImage
                lazyLoad={!(searchLoading || (!isMobile && index <= EAGER_LOADED_IMAGE_COUNT))}
                imageUrl={imageUrl || appConfig.productNoImageUrl}
                imageAlt={title || ''}
                mobileSize={'200px'}
                desktopSize={'200px'}></ResponsiveImage>
            {!searchLoading && (<>
                {!availableForSale && (<span
                    className={'search-page-list-item--badge sold-out'}>Sold out</span>)}
                {(availableForSale === 1 && !isNaN(compareAtPrice) && compareAtPrice > price) && (<span
                    className={'search-page-list-item--badge compare-price'}>Save {getSymbolFromCurrency(appConfig.storeCurrency)}{(compareAtPrice - price).toFixed(2)} {appConfig.storeCurrency}</span>)}
            </>)}
        </figure>
        <div
            className={'search-page--product-list-item-description'}>
            {vendor && <h5
                className={'text-uppercase mb-0'}>{vendor}</h5>
            }
            <h2
                className={'search-page--product-list-item-title'}><Link
                    to={!searchLoading ? `${productBasePath}/${handle}` : '#'}>{title ?? ''}</Link></h2>
            {availableForSale === 1 && !isNaN(compareAtPrice) && compareAtPrice > price && (<span
                className={'search-page-list-item--compare-price'}>{getSymbolFromCurrency(appConfig.storeCurrency)}{(compareAtPrice).toFixed(2)} {appConfig.storeCurrency}</span>)}
            <span
                className={'search-page--product-list-item-price'}>
                {(!isNaN(price) && price > 0) && (<>
                    {getSymbolFromCurrency(appConfig.storeCurrency)}{price.toFixed(2)} {appConfig.storeCurrency}
                </>)}
                {price === 0 && (<span
                    style={{
                        color: 'var(--secondary-color)',
                    }}>
                    Please call us for price on this product.
                </span>)}
            </span>
            <MiniProductReviewsBlock
                avgRating={avgRating}
                totalReviews={totalReviews}
                loading={searchLoading}
            />
            <button
                className={'search-page--product-list-item-cart'}
                onClick={onAddToCartClick}
                disabled={(cartUpdating || !availableForSale || quantity <= 0 || price === 0.0)}
            >
                {quantity > 0 && availableForSale ? 'Add to Cart' : 'Out of stock'}
            </button>

        </div>
    </button>);
}

SearchPageProductListItem.propTypes = {
    isMobile: bool,
    title: string,
    handle: string,
    imageUrl: any,
    price: oneOfType([number, string]),
    compareAtPrice: oneOfType([number, string]),
    availableForSale: oneOfType([number, bool]),
    searchLoading: bool,
    avgRating: oneOfType([number, bool]),
    totalReviews: oneOfType([number, bool]),
    isFeatured: bool,
    inventoryLevelId: number,
    availableQuantity: number,
    bypassNegativeQuantity: bool,
    view: string,
    index: number,
    vendor: string,
};

SearchPageProductListItem.defaultProps = {
    avgRating: 0,
    totalReviews: 0,
    isFeatured: false,
};